import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import useSWR, { SWRConfiguration, SWRResponse } from 'swr';

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
axios.defaults.headers['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers['Content-Type'] = 'application/json';
axios.defaults.timeout = 120000;

const configOptions = {
  revalidateOnFocus: false,
  revalidateIfStale: false,
  revalidateOnReconnect: false,
  onErrorRetry: (error: AxiosError, key, config, revalidate, retryCount) => {
    if (error.response?.status === 502) {
      return;
    }

    if (retryCount.retryCount >= 3) {
      return;
    }
  },
} as SWRConfiguration;

export type GetRequest = AxiosRequestConfig | null;

interface Return<Data, Error>
  extends Pick<SWRResponse<AxiosResponse<Data>, AxiosError<Error>>, 'isValidating' | 'error' | 'mutate'> {
  data: Data | undefined;
  response: AxiosResponse<Data> | undefined;
}

export interface Config<Data = unknown, Error = unknown>
  extends Omit<SWRConfiguration<AxiosResponse<Data>, AxiosError<Error>>, 'fallbackData'> {
  initialData?: Data;
}

export default function useRequest<Data = unknown, Error = unknown>(
  request: GetRequest,
  { initialData, ...config }: Config<Data, Error> = {},
): Return<Data, Error> {
  const {
    data: response,
    error,
    isValidating,
    mutate,
  } = useSWR<AxiosResponse<Data>, AxiosError<Error>>(request && JSON.stringify(request), () => axios(request!), {
    ...config,
    ...configOptions,
    fallbackData:
      initialData &&
      ({
        status: 200,
        statusText: 'InitialData',
        config: request! as AxiosRequestConfig,
        headers: {},
        data: initialData,
      } as AxiosResponse<Data>),
  });

  return {
    data: response && response.data,
    response,
    error,
    isValidating,
    mutate,
  };
}
